"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.authedFetch = exports.buildUnauthorizedResponseInterceptor = exports.buildAuthRequestInterceptor = exports.interceptFetch = exports.fetchWithTimeout = void 0;
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const utils_1 = require("./utils");
const web_log_1 = require("@snapchat/web-log");
const error_1 = require("./error");
const graphene_1 = __importDefault(require("@snapchat/graphene"));
const web_env_1 = require("@snapchat/web-env");
/**
 * make a fetch request with a specified timeout in ms
 * @param request fetch request object
 * @param timeout timeout (ms)
 * @param requestMetricPath request path for metrics
 * @param metricsPrefix graphene metrics prefix
 */
async function fetchWithTimeout(request, timeout, requestMetricPath, metricsPrefix) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        web_log_1.log.warn(`request timeout after ${timeout}ms. metrics group: ${metricsPrefix}. endpoint:`, request.url);
        void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${requestMetricPath}.timeout`);
        controller.abort();
    }, timeout);
    const response = await fetch(request, { signal: controller.signal }).catch((err) => {
        clearTimeout(timeoutId);
        if (controller.signal.aborted) {
            return new Response(null, { status: 408, statusText: "Request timeout" });
        }
        else {
            throw err;
        }
    });
    clearTimeout(timeoutId);
    return response;
}
exports.fetchWithTimeout = fetchWithTimeout;
/**
 * return a `fetch` function with interceptions for added functionalities
 * name request name for metrics
 * metricsPrefix graphene metrics prefix
 * requestInterceptors functions executed pre-request
 * responseInterceptors functions executed post-request
 * requestPathname request path name for metrics (e.g. fetch_sso)
 * timeout request timeout in ms
 */
const interceptFetch = ({ name, metricsPrefix, requestInterceptors = [], responseInterceptors = [], requestPathname, timeout = 60000, }) => {
    const loggerPrefix = `[Network:${name}]`;
    return async (requestInfo, requestInit) => {
        let request = new Request(requestInfo, requestInit);
        for (const curReqInterceptor of requestInterceptors) {
            request = await curReqInterceptor(request, loggerPrefix);
        }
        const requestUrl = new URL(request.url);
        const requestPath = (0, utils_1.urlToMetricPath)(requestUrl);
        const requestMetricPath = requestPathname ? `${requestPathname}.${requestPath}` : requestPath;
        const fetchStart = performance.now();
        let response;
        let error;
        try {
            response = await fetchWithTimeout(request, timeout, requestMetricPath, metricsPrefix);
        }
        catch (e) {
            error = e;
        }
        const duration = Math.round(performance.now() - fetchStart);
        const responseUrl = response?.url ? new URL(response.url) : "";
        const responseMetricPath = responseUrl ? (0, utils_1.urlToMetricPath)(responseUrl) : "";
        void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${requestMetricPath}.attempt`);
        // log 429 once
        if (response?.status === 429) {
            const metricsName = `${metricsPrefix}.network_${requestMetricPath}_session_rate_limit`;
            (0, utils_1.callOnlyOnce)(() => {
                void (0, web_graphene_reporting_1.incrementCounter)(metricsName);
            }, requestMetricPath);
        }
        if (response === undefined) {
            web_log_1.log.info("network error while fetching", request.url, error);
            // fail early during SSR, don't report client-side metrics etc.
            if ((0, web_env_1.isRunningOnServer)()) {
                throw new Error(`received no response with unknown error. ${String(error)}`);
            }
            const onlineStatus = String(navigator.onLine);
            const errorName = error instanceof Error ? error.name.toLocaleLowerCase() : "unknown";
            void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${requestMetricPath}.network_error`, 1, {
                online: onlineStatus,
                error_name: errorName,
            });
            if (error instanceof Error) {
                if ((0, error_1.isNetworkError)(error, metricsPrefix)) {
                    web_log_1.log.info(`${error.name}: ${error.message}. Request ${request.url} couldn't reach the host, with online status: ${onlineStatus}`);
                }
                else if ((0, error_1.isAbortError)(error)) {
                    web_log_1.log.info(`${error.name}: ${error.message}. Request ${request.url} was aborted, with online status: ${onlineStatus}`);
                }
            }
            throw new Error(`received no response with unknown error. ${String(error)}`);
        }
        let pathMetricSegment = requestMetricPath;
        if (requestMetricPath !== responseMetricPath) {
            // put both request path and response path in metric
            pathMetricSegment = `${requestMetricPath}_redirect_to_${responseMetricPath}`;
            void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${responseMetricPath}.attempt`);
            void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${requestMetricPath}.redirect_to.${responseMetricPath}`);
        }
        const responseSize = parseInt(response.headers.get("content-length") ?? "");
        if (responseSize) {
            void (0, web_graphene_reporting_1.reportHistogram)(`${metricsPrefix}.network.${pathMetricSegment}.response_size`, responseSize);
        }
        graphene_1.default.addTimer({
            metricsName: `${metricsPrefix}.network.${pathMetricSegment}.latency`,
            milliSec: duration,
        });
        graphene_1.default.addTimer({
            metricsName: `${metricsPrefix}.network_latency`,
            milliSec: duration,
            dimensions: {
                api: pathMetricSegment,
            },
        });
        void (0, web_graphene_reporting_1.incrementCounter)(`${metricsPrefix}.network.${pathMetricSegment}.code.${response.status.toString()}`);
        for (const curResInterceptor of responseInterceptors) {
            response = await curResInterceptor(response, loggerPrefix);
        }
        return response;
    };
};
exports.interceptFetch = interceptFetch;
/**
 * factory for request interceptors
 * @param ssoToken authed SSO token
 */
const buildAuthRequestInterceptor = (ssoToken) => (request) => {
    request.headers.set("Authorization", `Bearer ${ssoToken}`);
    return new Request(request, { credentials: "include" });
};
exports.buildAuthRequestInterceptor = buildAuthRequestInterceptor;
/**
 * factory for response interceptors
 * @param onUnauthorizedError unauthorised error callback function
 */
const buildUnauthorizedResponseInterceptor = (onUnauthorizedError) => (response) => {
    if ([401, 403].includes(response.status)) {
        web_log_1.log.info("Got unauthorised error from the server.");
        onUnauthorizedError?.();
    }
    return response;
};
exports.buildUnauthorizedResponseInterceptor = buildUnauthorizedResponseInterceptor;
/**
 * extension of the regular `fetch` method
 * @param ssoToken bearer token attached with requests
 * @param metricsPrefix graphene metrics prefix
 * @param requestPathname request pathname (e.g. fetch_sso)
 */
const authedFetch = (ssoToken, metricsPrefix, requestPathname) => (0, exports.interceptFetch)({
    name: "default-authed-fetch",
    metricsPrefix: metricsPrefix,
    requestInterceptors: [(0, exports.buildAuthRequestInterceptor)(ssoToken)],
    responseInterceptors: [(0, exports.buildUnauthorizedResponseInterceptor)()],
    requestPathname: requestPathname,
});
exports.authedFetch = authedFetch;
//# sourceMappingURL=http.js.map